import { TPackingApiStatus } from '../../ba-app';
import { TBctxHeaderItem, TBctxItem, TTagInfo } from '../common/api.types';
import { TAppDetailsCpi, TPaginationInfo } from '../fabric-inspection/api.types';
import { IDefectList, IRejectList } from '../master-data.types';

type TCpiActionCommon = {
  id: string;
  groupId?: string; // Set when multiple panels are added in a single batch (multi-panel selection); used to undo all panels in the batch together
  dateTime: number;
  panelIdentifier: string; // id_panelNumber
  round: number;
  eventType: 'FORWARD' | 'UNDO';
};

export type TCpiDefectiveOrRecutForwardActionTypes = 'DEFECTIVE' | 'RECUT';

export type TCpiRectifiedOrReplacedForwardActionTypes = 'RECTIFIED' | 'REPLACED';

export type TCpiActionTypes = TCpiDefectiveOrRecutForwardActionTypes | TCpiRectifiedOrReplacedForwardActionTypes;

export type TCpiDefectiveOrRecutAction = TCpiActionCommon & {
  type: TCpiDefectiveOrRecutForwardActionTypes;
  issues: IDefectList[] | IRejectList[];
};

export type TCpiRectifiedOrReplacedAction = TCpiActionCommon & {
  type: TCpiRectifiedOrReplacedForwardActionTypes;
};

export type TCpiAddFttAction = Pick<TCpiActionCommon, 'dateTime'> & {
  type: 'FTT';
  value: number;
};

export type TCpiAllActionTypes = TCpiDefectiveOrRecutAction | TCpiRectifiedOrReplacedAction;

export type TCpiActions = TCpiAllActionTypes[];

export type TCpiActionsWithBctxInfo = {
  bctx: TBctxHeaderItem[];
  actions: TCpiActions;
}[];

export type TAuditDetailRow = {
  actionName: TCpiActionTypes;
  actionType: 'ADD' | 'UNDO';
  date: number;
  panelNumber?: number;
};

export type TCutPanelInspectionComplete = {
  bctxItems: TBctxItem[];
  fttCount: number;
  replacedCount: number;
  rectifiedCount: number;
  recuts: TCpiIssuesList[];
  defectives: TCpiIssuesList[];
};

export type TCpiActionsGroupedById = {
  [id: string]: TCpiActions;
};

type TCpiIssueTypes = 'RECUT' | 'DEFECTIVES';

type TCpiIssuesList = {
  panelNumber?: string;
  list: TCpiIssueListItem[];
};

type TCpiIssueListItem = {
  id: string;
  name: string;
};

// *********************************

// Fetch Cut Panel Inspection info
export type TREQUEST_GetCutPanelInspectionInfo = {
  // Also used to verify if the tag is inspected previously or not by sending TAG_ID as filter
  appDetails: TAppDetailsCpi;
  filters: TCpiFilter[];
};

export type TCpiFilter = TCpiFilterTypesWithValues | TTagFieldFilter;

export type TCpiFilterTypesWithValues = {
  filterType: ECpiFilterTypes;
  values: string[] | number[];
};

// This is a generic tag field filter, it can be used for any key from kv details, here key field is key from kv details
export type TTagFieldFilter = {
  filterType: 'TAG_FIELD';
  key: string;
  values: string[];
};

export enum ECpiFilterTypes {
  DATE = 'DATE',
  TAG_ID = 'TAG_ID',
  VENDOR = 'VENDOR',
  TOGGLE_FILTER = 'TOGGLE_FILTER',
  STATUS = 'STATUS',
}

export type TRESPONSE_GetCutPanelInspectionInfo = {
  requestId: string;
  status: TPackingApiStatus;
  errors: TCpiCustomError[];
  cutPanelInspectionInfoCount: TCutPanelInspectionInfoCount;
};

type TCpiCustomError = {
  type: 'CUT_PANEL_INSPECTION_ERROR' | 'TAG_ERROR';
  message: string;
};

export type TCutPanelInspectionInfoCount = {
  ftt: number;
  defectives: number;
  recut: number;
  toBeRectifiedOrReplaced: number;
};

// *********************************

export type TREQUEST_ComponentTagDetails = {
  subject_key: string;
  displayTag: string;
};

// Fetch Component Tag details with WIP info [CPI] for CPM changes
export type TREQUEST_ComponentTagDetailsWithWipInfo = {
  subjectKey: string;
  subject: string;
  domRole: string; //for domeRole send dnrk from getAppDetailsWithAdditionalInfo
  componentDisplayTagId: string;
};

export type TRESPONSE_ComponentTagDetailsWithWipInfo = {
  status: TPackingApiStatus;
  tagDetails: TTagInfo[];
  remainingWip: number;
  error: string;
};

// Fetch Component Tags list by Master Bundle Tag
export type TREQUEST_ComponentTagsListByBundleTag = {
  factoryId: string;
  cutDisplayTag: string;
};

type TRESPONSE_CutTagMinimalInfo = {
  factoryId: string;
  cutTag: TMasterTagMinimalInfo;
};

export type TRESPONSE_ComponentTagsListByBundleTag = TRESPONSE_CutTagMinimalInfo & {
  componentTags: TComponentTagMinimalInfo[];
  errorResponseDTO?: TComponentTagErrorDTO;
};

// Fetch Component Tags list by Master Bundle Tag for CPM
export type TREQUEST_ComponentTagsListByMasterBundleTagCPM = TREQUEST_ComponentTagsListByBundleTag & {
  appDetails: TAppDetailsCpi;
};

type TCpiInspectionStatus = 'IN_PROGRESS' | 'COMPLETED';

export type TInspectionStatusForTable = TCpiInspectionStatus | 'NOT_STARTED';

export type TRESPONSE_ComponentTagsListByMasterBundleTagCPM = TRESPONSE_CutTagMinimalInfo & {
  componentTags: TComponentTagMinimalInfoCPM[];
  errorResponseDTO?: {
    ERROR: string;
  };
};

export type TRESPONSE_ComponentTagsListResponseDTO =
  | TRESPONSE_ComponentTagsListByBundleTag
  | TRESPONSE_ComponentTagsListByMasterBundleTagCPM;

export type TComponentTagMinimalInfoCPM = TComponentTagMinimalInfo & {
  cpiInspectionDTO?: {
    inspectionStatus: TCpiInspectionStatus;
    ftt: number;
    actualQty: number;
    replacedOrRectifiedPanelIdentifiers: string[];
  };
};

export type TComponentTagMinimalInfoType = TComponentTagMinimalInfo | TComponentTagMinimalInfoCPM;

export type TComponentTagsDTOFromMasterTag = TComponentTagMinimalInfo[] | TComponentTagMinimalInfoCPM[];

type TMasterTagMinimalInfo = {
  cutTagId: string;
  displayCutTagId: string;
  cutTagKVDetails: TTagInfo[];
};

export type TComponentTagMinimalInfo = {
  componentTagId: string;
  displayComponentTagId: string;
  componentTagKVDetails: TTagInfo[];
};

type TComponentTagErrorDTO = {
  error: string;
  componentTagError: EComponentTagErrors;
};

enum EComponentTagErrors {
  BUNDLE_GUIDE_NOT_FOUND = 'BUNDLE_GUIDE_NOT_FOUND',
  BCTX_FIELDS_NOT_CONFIGURED = 'BCTX_FIELDS_NOT_CONFIGURED',
  INVALID_COMPONENT_TAG = 'INVALID_COMPONENT_TAG',
  CUT_TAG_MAPPING_MISSING = 'CUT_TAG_MAPPING_MISSING',
}

// *********************************

// Save CPI details
export type TREQUEST_SaveCutPanelInspectionInfo = {
  appDetails: TAppDetailsCpi;
  item: TCutPanelInspectionRequestItem;
  eventTime: number;
};

export type TCutPanelInspectionRequestItem = {
  tagId: string;
  ftt: number;
  recut: TCpiDefect[];
  defectives: TCpiDefect[];
  replacedOrRectifiedPanelIdentifier: string[]; // [uniqueId_panelNumber]
};

export type TCpiDefect = {
  id: string;
  name: string;
  qty: number;
  panelIdentifiers: string[]; // [uniqueId_panelNumber]
};

export type TRESPONSE_SaveCutPanelInspectionInfo = {
  requestId: string;
  status: TPackingApiStatus;
  errors: TCpiCustomError[];
};

export type TCpiDefectiveOrRecutActionWithSelectionInfo = TCpiDefectiveOrRecutAction & {
  bctx: TBctxHeaderItem[];
  isSelected: boolean;
  isDisabled: boolean;
};

// *********************************

// Complete Inspection
export type TREQUEST_CompleteCutPanelInspectionInfo = TREQUEST_SaveCutPanelInspectionInfo;

export type TRESPONSE_CompleteCutPanelInspectionInfo = TRESPONSE_SaveCutPanelInspectionInfo;

// *********************************

// Save to be rectified replaced components
export type TREQUEST_SaveToBeRectifiedReplacedComponents = {
  appDetails: TAppDetailsCpi;
  items: TCutPanelInspectionRequestItem[];
  eventTime: number;
};

export type TRESPONSE_SaveToBeRectifiedReplacedComponents = TRESPONSE_SaveCutPanelInspectionInfo;

// *********************************

// Fetch All Transactions and ToBeRectifiedAndReplaced
// send IN_PROGRESS status filter to get toBeRectifiedReplaced items
export type TREQUEST_CutPanelInspectionAllTransactions = {
  appDetails: TAppDetailsCpi;
  filters: TCpiFilter[];
  pageNo: number;
  pageSize: number;
};

export type TRESPONSE_CutPanelInspectionAllTransactions = {
  requestId: string;
  status: TPackingApiStatus;
  errors?: TCpiCustomError[];
  selectedFilters: TCpiFilter[];
  supportedFilters: Record<string, string[]>;
  cutPanelInspectionItemInfoList: TCutPanelInspectionItemInfoList[];
  paginationInfo: TPaginationInfo;
};

export type TCutPanelInspectionItemInfoList = {
  tagId: string;
  tagDetails: TTagInfo[];
  inspectionStatus: 'COMPLETED' | 'IN_PROGRESS';
  ftt: number;
  actual: number;
  recut: TCpiDefect[];
  defectives: TCpiDefect[];
  updatedTime: number;
};

// *********************************

export type TCutPanelInspectionDB = {
  nextCacheResetTimestamp: number;
  recentRecuts: {
    default: IRejectList[];
  };
  counts: {
    res?: TCutPanelInspectionInfoCount;
  };
  selectedMasterCutDisplayTag: string | null;
};

export type TMultiPanelsSelectionWithSameIssuesModal = {
  selectedList: IDefectList[] | IRejectList[];
  selectedPanelNumbers: string[];
};